import { ensureOk } from "@/api/apiError";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { components } from "@/api/generated-types";
import type { SkillDetail } from "@/types";

export type PaginatedSkills =
  components["schemas"]["PaginatedResponse_SkillRead_"];

export interface SkillQuery {
  q?: string;
  category?: string;
  harness?: string;
  skip?: number;
  limit?: number;
}

function buildQueryString(params: SkillQuery): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.category) search.set("category", params.category);
  if (params.harness) search.set("harness", params.harness);
  search.set("skip", String(params.skip ?? 0));
  search.set("limit", String(params.limit ?? 12));
  return `?${search.toString()}`;
}

async function fetchSkills(params: SkillQuery): Promise<PaginatedSkills> {
  const response = await httpClient(`/skills${buildQueryString(params)}`);
  await ensureOk(response);
  return response.json() as Promise<PaginatedSkills>;
}

async function fetchSkillFacet(
  path: "categories" | "harnesses",
): Promise<string[]> {
  const response = await httpClient(`/skills/${path}`);
  await ensureOk(response);
  return response.json() as Promise<string[]>;
}

async function fetchSkill(slug: string): Promise<SkillDetail> {
  const response = await httpClient(`/skills/${encodeURIComponent(slug)}`);
  await ensureOk(response);
  return response.json() as Promise<SkillDetail>;
}

/** Paginated skill catalogue (ACTIVE only) — search/filter/pagination resolved server-side. */
export function useSkills(params: SkillQuery = {}) {
  return useQuery({
    queryKey: ["skills", params],
    queryFn: () => fetchSkills(params),
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Distinct included-skills (categories) across active skills — powers the filter chips. */
export function useSkillCategories() {
  return useQuery({
    queryKey: ["skills", "categories"],
    queryFn: () => fetchSkillFacet("categories"),
    staleTime: 5 * 60 * 1000,
  });
}

/** Distinct compatible harnesses across active skills — powers the filter chips. */
export function useSkillHarnesses() {
  return useQuery({
    queryKey: ["skills", "harnesses"],
    queryFn: () => fetchSkillFacet("harnesses"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSkill(slug: string | undefined) {
  return useQuery({
    queryKey: ["skills", slug],
    queryFn: () => fetchSkill(slug as string),
    enabled: Boolean(slug),
    staleTime: 60 * 1000,
  });
}

export type SkillProposalInput = {
  name: string;
  shortDescription: string;
  fullDescription?: string;
  includedSkills?: string[];
  compatibleHarnesses?: string[];
  sourceType: "ZIP" | "GITHUB";
  repositoryUrl?: string;
  zipFile?: File;
};

function buildProposalFormData(input: SkillProposalInput): FormData {
  const formData = new FormData();
  formData.append("name", input.name);
  formData.append("shortDescription", input.shortDescription);
  if (input.fullDescription) {
    formData.append("fullDescription", input.fullDescription);
  }
  if (input.includedSkills?.length) {
    formData.append("includedSkills", input.includedSkills.join(","));
  }
  if (input.compatibleHarnesses?.length) {
    formData.append("compatibleHarnesses", input.compatibleHarnesses.join(","));
  }
  formData.append("sourceType", input.sourceType);
  if (input.repositoryUrl) {
    formData.append("repositoryUrl", input.repositoryUrl);
  }
  if (input.zipFile) {
    formData.append("zipFile", input.zipFile);
  }
  return formData;
}

async function createSkillProposal(
  input: SkillProposalInput,
): Promise<SkillDetail> {
  const response = await httpClient("/skills/proposals", {
    method: "POST",
    body: buildProposalFormData(input),
  });
  await ensureOk(response);
  return response.json() as Promise<SkillDetail>;
}

/** Submits a skill proposal (multipart: ZIP upload or GitHub source). The catalogue query is
 * invalidated but the new skill stays `pending` (invisible in the public list) until an admin
 * approves it (US-12). */
export function useCreateSkillProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSkillProposal,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}

/** Downloads a skill ZIP: PPR/Prod returns a JSON presigned S3 URL (opened in a new tab), dev
 * local streams the ZIP bytes directly (saved via an object URL), mirroring the legacy
 * `downloadSkillZip` behaviour. */
export async function downloadSkill(
  slug: string,
  version?: string,
): Promise<void> {
  const suffix = version ? `?version=${encodeURIComponent(version)}` : "";
  const response = await httpClient(
    `/skills/${encodeURIComponent(slug)}/download${suffix}`,
  );
  await ensureOk(response);

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const { url } = (await response.json()) as {
      url: string;
      fileName: string;
    };
    window.open(url, "_blank");
    return;
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const disposition = response.headers.get("content-disposition") ?? "";
  const fileNameMatch = /filename="?([^"]+)"?/i.exec(disposition);
  const fileName =
    fileNameMatch?.[1] ?? `${slug}${version ? `-${version}` : ""}.zip`;

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
